import { Fragment, ReactNode } from 'react';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import { currency } from '../formatters';

// Shared skeleton for every "Projected payout" cell tooltip, reimbursement and
// indemnity alike. Three blocks, in the order an advisor reads them:
//
//   Paid by policy            what the policy put in this month
//   ─────
//   Care cost covered         where those dollars landed
//   Room & board covered
//   ─────
//   Out of pocket             what the client still pays, split care vs R&B
//     · care cost
//     · room & board
//
// The previous shape led with the costs and buried the payout's destination, so
// a reader couldn't see that indemnity cash had covered part of room & board —
// the split this block exists to surface. Strategies supply their own product
// wording and any extra lines; the skeleton keeps them all in one order.

export interface PayoutSubLine {
  label: ReactNode;
  value: number;
  // 'muted' renders the plain grey used for explanatory splits; 'oop' renders
  // the rose used for money the client is still on the hook for.
  tone?: 'muted' | 'oop';
}

export interface PayoutBreakdownProps {
  paidByPolicy: number;
  // Small annotation appended to the "Paid by policy" label — e.g. STC's
  // pro-rated cash note ("15d left in benefit period").
  paidNote?: string;
  // Optional split of the headline payout (e.g. indemnity cash vs a retroactive
  // waiting-period reimbursement). Omit when the payout is a single stream.
  paidSubLines?: PayoutSubLine[];
  careCovered: number;
  // Annotation on the "Care cost covered" label — e.g. "20 of 30 days eligible".
  // Deliberately not a value row: the eligible COST is not a component of what
  // was covered (on a capped month it exceeds it), and the difference is already
  // itemized below as the over-cap out-of-pocket line.
  careCoveredNote?: string;
  // Sub-lines under "Care cost covered" — genuine components of it, e.g. the
  // portion drained from an indemnity surplus reserve rather than paid as this
  // month's cash.
  careCoveredSubLines?: PayoutSubLine[];
  // Indemnity only: this month's cash above the eligible care cost that covered
  // out-of-pocket room & board. Reimbursement products pass 0 and the line is
  // dropped — they pay eligible care only.
  roomAndBoardCovered?: number;
  // Indemnity only: cash that covered neither and banked into the reserve.
  // Shown so "Paid by policy" reconciles with the lines beneath it.
  surplusBanked?: number;
  careOutOfPocket: number;
  // Why the care shortfall exists (over the monthly cap, waiting period, <2
  // ADL, before policy start …). Rendered indented under the care OOP line.
  careOutOfPocketReasons?: PayoutSubLine[];
  roomAndBoardOutOfPocket: number;
  // Set when room & board applies to this row at all, so a $0 out-of-pocket
  // room & board line still renders (it means "fully covered", which is
  // information) while non-IL rows drop it entirely.
  hasRoomAndBoard: boolean;
}

// Called directly rather than rendered as <Line />, so the spans land inline in
// the returned fragment. Strategy tests walk this tree with a shallow
// `props.children` reader that can't see through a function component — an
// element here would make every row invisible to them.
function Line({
  label,
  value,
  tone,
  indent,
  rowKey,
}: {
  label: ReactNode;
  value: number;
  tone?: 'muted' | 'oop';
  indent?: 1 | 2;
  rowKey?: string;
}): ReactNode {
  const labelClass =
    indent === 2 ? 'pl-4 text-gray-500' : indent === 1 ? 'pl-2 text-gray-400' : 'text-gray-300';
  const valueClass =
    tone === 'oop'
      ? 'tabular-nums text-rose-300'
      : tone === 'muted'
        ? 'tabular-nums text-gray-400'
        : 'font-semibold tabular-nums';
  return (
    <Fragment key={rowKey}>
      <span className={labelClass}>{label}</span>
      <span className={valueClass}>{currency(value)}</span>
    </Fragment>
  );
}

const rule = (key: string) => (
  <span
    key={key}
    className="col-span-2 my-1 border-t border-gray-700"
  />
);

export function payoutBreakdownRows({
  paidByPolicy,
  paidNote,
  paidSubLines = [],
  careCovered,
  careCoveredNote,
  careCoveredSubLines = [],
  roomAndBoardCovered = 0,
  surplusBanked = 0,
  careOutOfPocket,
  careOutOfPocketReasons = [],
  roomAndBoardOutOfPocket,
  hasRoomAndBoard,
}: PayoutBreakdownProps): ReactNode {
  return (
    <>
      {Line({
        label: (
          <>
            Paid by policy
            {paidNote ? <span className="text-gray-500"> &middot; {paidNote}</span> : null}
          </>
        ),
        value: paidByPolicy,
      })}
      {paidSubLines.map((l, i) =>
        Line({ rowKey: `paid-${i}`, label: l.label, value: l.value, tone: l.tone ?? 'muted', indent: 1 }),
      )}
      {rule('rule-paid')}

      {Line({
        label: (
          <>
            Care cost covered
            {careCoveredNote ? (
              <span className="text-gray-500"> &middot; {careCoveredNote}</span>
            ) : null}
          </>
        ),
        value: careCovered,
      })}
      {careCoveredSubLines.map((l, i) =>
        Line({ rowKey: `care-${i}`, label: l.label, value: l.value, tone: l.tone ?? 'muted', indent: 1 }),
      )}
      {hasRoomAndBoard &&
        Line({ label: 'Room & board covered', value: roomAndBoardCovered })}
      {surplusBanked > 0 && Line({ label: 'Banked as surplus', value: surplusBanked })}
      {rule('rule-covered')}

      <span className="col-span-2 text-gray-300">Out of pocket</span>
      {Line({ label: '\u00b7 care cost', value: careOutOfPocket, tone: 'oop', indent: 1 })}
      {careOutOfPocketReasons.map((l, i) =>
        Line({ rowKey: `oop-${i}`, label: l.label, value: l.value, tone: l.tone ?? 'oop', indent: 2 }),
      )}
      {hasRoomAndBoard &&
        Line({
          label: '\u00b7 room & board',
          value: roomAndBoardOutOfPocket,
          tone: 'oop',
          indent: 1,
        })}
    </>
  );
}

// Per-reason care out-of-pocket lines, built from the calc's own day breakdown
// so a waiting-period-only row never claims "<2 ADL" contributed. Shared by
// every strategy: the reasons are engine facts, not product behaviour.
export function careOutOfPocketReasons({
  month,
  overCapAmount,
  overCapLabel = '· over monthly cap',
  extra = [],
}: {
  month: MonthlyEntry;
  overCapAmount: number;
  overCapLabel?: ReactNode;
  extra?: PayoutSubLine[];
}): PayoutSubLine[] {
  const dailyCost = month.facts.dailyCareCost;
  // `events` is non-optional on a real projection row; the fallback only keeps
  // partial test fixtures from throwing the whole tooltip away.
  const breakdown = month.events?.ineligibleDayBreakdown ?? {
    lt2AdlDays: 0,
    inWpDays: 0,
    ilNotCoveredDays: 0,
    prePolicyDays: 0,
  };
  const lines: PayoutSubLine[] = [];
  if (overCapAmount > 0) lines.push({ label: overCapLabel, value: overCapAmount });
  lines.push(...extra);
  if (breakdown.inWpDays > 0) {
    lines.push({
      label: (
        <>
          &middot; waiting period{' '}
          <span className="text-gray-600">&middot; {breakdown.inWpDays}d</span>
        </>
      ),
      value: breakdown.inWpDays * dailyCost,
    });
  }
  if (breakdown.lt2AdlDays > 0) {
    lines.push({
      label: (
        <>
          &middot; under 2 ADLs{' '}
          <span className="text-gray-600">&middot; {breakdown.lt2AdlDays}d</span>
        </>
      ),
      value: breakdown.lt2AdlDays * dailyCost,
    });
  }
  if (breakdown.prePolicyDays > 0) {
    lines.push({
      label: (
        <>
          &middot; before policy start{' '}
          <span className="text-gray-600">&middot; {breakdown.prePolicyDays}d</span>
        </>
      ),
      value: breakdown.prePolicyDays * dailyCost,
    });
  }
  return lines;
}

// The reimbursement half of every two-mode payout tooltip. Reimbursement pays
// eligible incurred care only, so nothing of it reaches room & board — the
// covered line is always $0 there, and the whole room & board cost stays
// out-of-pocket.
export function reimbursementPayoutRows({
  month,
  overCapLabel,
  extraOopReasons = [],
}: {
  month: MonthlyEntry;
  overCapLabel?: ReactNode;
  extraOopReasons?: PayoutSubLine[];
}): ReactNode {
  const cost = month.facts.effectiveCareCost;
  const dailyCost = month.facts.dailyCareCost;
  const paid = month.deltas.payout.ltcCoveragePaid;
  const eligibleDays = month.facts.coverageEligibleDays;
  const isPartial = eligibleDays < DAYS_IN_MONTH;
  const eligibleCost = isPartial ? eligibleDays * dailyCost : cost;
  const roomAndBoard = month.facts.roomAndBoardCost ?? 0;

  return (
    <>
      {payoutBreakdownRows({
        paidByPolicy: paid,
        careCovered: paid,
        careCoveredNote: isPartial ? `${eligibleDays} of ${DAYS_IN_MONTH} days eligible` : undefined,
        roomAndBoardCovered: 0,
        careOutOfPocket: Math.max(cost - paid, 0),
        careOutOfPocketReasons: careOutOfPocketReasons({
          month,
          overCapAmount: Math.max(eligibleCost - paid, 0),
          overCapLabel,
          extra: extraOopReasons,
        }),
        roomAndBoardOutOfPocket: roomAndBoard,
        hasRoomAndBoard: roomAndBoard > 0,
      })}
    </>
  );
}
